import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useTheme } from "@/lib/ThemeContext";
import PullToRefresh from "@/components/PullToRefresh";

const AnimatedElement = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setIsVisible(true), delay);
        observer.unobserve(el);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      } ${className}`}
    >
      {children}
    </div>
  );
};

function ProductCard({ product, index }) {
  const { dark } = useTheme();
  
  return (
    <AnimatedElement delay={index * 100}>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3 }}
        className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      >
        <div className="relative aspect-[4/3] bg-secondary/20 overflow-hidden">
          <img
            src={product.image_urls?.[0] || product.image_url || "https://via.placeholder.com/400x300?text=No+Image"}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          {product.badge && product.badge !== "None" && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-bold px-2.5 py-1 rounded-full shadow">
              {product.badge}
            </span>
          )}
        </div>
        
        <div className="p-5">
          <h3 className="font-bold text-xl mb-2 text-foreground group-hover:text-primary transition-colors">
            {product.name}
          </h3>
          
          {(product.short_description || product.description) && (
            <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
              {product.short_description || product.description}
            </p>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-primary">
              ${product.price}
            </span>
            <Link
              to={`/product/${product.id}`}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-4 py-2 rounded-full hover:bg-primary/90 transition-colors text-sm"
            >
              View Details
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </AnimatedElement>
  );
}

export default function WaterSlidesPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(() => {
    return base44.entities.Product.filter({ category: "Water Slides", active_status: true })
      .then(setProducts)
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchProducts().finally(() => setLoading(false));
  }, [fetchProducts]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchProducts}>
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <AnimatedElement>
            <h1 className="font-extrabold text-4xl sm:text-5xl md:text-6xl text-foreground mb-4">
              Water Slide Rentals
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Fun, clean, and ready for your next party.
            </p>
          </AnimatedElement>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No water slides available at the moment.</p>
              <Link to="/BrowseRentals" className="mt-4 inline-block text-primary hover:underline">
                Browse other rentals →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
    </PullToRefresh>
  );
}